import {useState} from 'react'
import {differenceInCalendarMonths, parseISO} from 'date-fns'
import {Space} from 'antd'

import styles from './RequestForecastForm.module.scss'
import useStore from 'base/store'
import {useCreateForecast} from 'common/api'
import {
  Button,
  Box,
  Text,
  CentsMoney,
  RotateArrowThirdIcon,
  RotateArrowFourthIcon,
} from 'common/components'
import {
  formatDate,
  formatMoney,
  maybePluralize,
  totalNumberFromArray,
  getInitialForecast,
  isItemDrawdown,
  isItemRepayment,
} from 'common/helpers'
import {
  ForecastItem as ForecastItemInterface,
  ForecastFormItem,
} from 'common/types'
import {RequestForecastList, RequestForecastItemAdd} from '..'

interface RequestForecastFormProps {
  request: any
  forecast: ForecastItemInterface[]
  onClose: () => void
}

interface CreateForcastProps {
  project: any
  openDate: Date
  maturityDate: Date
  loanValue: number
  onClose: () => void
}

interface UpdateForecastProps {
  requestUuid: string
  forecast: ForecastItemInterface[]
  project: any
  openDate: Date
  loanValue: number
}

interface SummaryRowProps {
  isDrawdowns?: boolean
  count: number
  total: number
  loanValue?: number
}

export default function RequestForecastForm({
  request,
  forecast,
  onClose,
}: RequestForecastFormProps) {
  const project = request.project
  const openDate = parseISO(request.openDate)
  const maturityDate = parseISO(request.maturityDate)
  const {loanValue} = request

  if (!!forecast?.length)
    return (
      <UpdateForecast
        forecast={forecast}
        requestUuid={request.uuid}
        project={project}
        openDate={openDate}
        loanValue={loanValue}
      />
    )

  return (
    <CreateForcast
      project={project}
      openDate={openDate}
      maturityDate={maturityDate}
      loanValue={loanValue}
      onClose={onClose}
    />
  )
}

const CreateForcast = ({
  project,
  openDate,
  maturityDate,
  loanValue,
  onClose,
}: CreateForcastProps) => {
  const [newForecast, setNewForecast] = useState<ForecastFormItem[]>(
    getInitialForecast(loanValue, openDate, maturityDate)
  )
  const setForecastCreatedProjects = useStore(
    (state) => state.setForecastCreatedProjects
  )
  const {mutate: createForecast, isMutating: isCreateForecastMutating} =
    useCreateForecast(project.uuid, onClose, setForecastCreatedProjects)

  const handleFinish = () => {
    const newForecastToServer: any = {
      project: project.uuid,
      items: newForecast.map((item, index) => {
        const newItem: any = {
          amount: item.amount,
          type: item.type,
          date: formatDate(item.date, 'server'),
        }
        if (index === 0) {
          newItem.initialDrawdown = true
        }
        return newItem
      }),
    }
    createForecast(newForecastToServer)
  }

  const startDate = newForecast[0].date
  const endDate = newForecast.at(-1).date
  const months = differenceInCalendarMonths(endDate, startDate)
  const currentLoanValue = loanValue
  const drawdowns = newForecast.filter((item) => isItemDrawdown(item))
  const repayments = newForecast.filter((item) => isItemRepayment(item))
  const drawdownsTotalAmount = totalNumberFromArray(drawdowns, 'amount')
  const isOverLimit = drawdownsTotalAmount > currentLoanValue

  return (
    <>
      <Text align="center" mb={24}>
        This forecast is created automatically. The entire loan amount is evenly
        distributed over the number of months. You can add and remove estimates
        and adjust the current table.
      </Text>
      <RequestForecastList
        forecast={newForecast}
        months={months}
        openDate={openDate}
        loanValue={currentLoanValue}
        setNewForecast={setNewForecast}
      />
      <RequestForecastItemAdd
        minDate={openDate}
        forecast={newForecast}
        loanValue={currentLoanValue}
        setNewForecast={setNewForecast}
      />
      <Box>
        <SummaryRow
          isDrawdowns
          count={drawdowns.length}
          total={drawdownsTotalAmount}
          loanValue={currentLoanValue}
        />
        {Boolean(repayments.length) && (
          <SummaryRow
            count={repayments.length}
            total={totalNumberFromArray(repayments, 'amount')}
          />
        )}
      </Box>
      <Box align="right" mt={24}>
        <Button
          loading={isCreateForecastMutating}
          disabled={!drawdowns.length || isOverLimit}
          onClick={handleFinish}
        >
          Save forecast
        </Button>
      </Box>
    </>
  )
}

const UpdateForecast = ({
  requestUuid,
  project,
  openDate,
  loanValue,
  forecast,
}: UpdateForecastProps) => {
  const currentForecast: ForecastFormItem[] = forecast.map((item) => ({
    uuid: item.uuid,
    type: item.type,
    date: parseISO(item.value.date),
    amount: item.value.amount,
    initialDrawdown: !!item.initialDrawdown
  }))
  const drawdowns = currentForecast.filter((item) => isItemDrawdown(item))
  const repayments = currentForecast.filter((item) => isItemRepayment(item))
  const drawdownsTotalAmount = totalNumberFromArray(drawdowns, 'amount')
  const startDate = currentForecast[0].date
  const endDate = currentForecast.at(-1).date
  const months = differenceInCalendarMonths(endDate, startDate)

  return (
    <>
      <RequestForecastList
        forecast={currentForecast}
        projectUuid={project.uuid}
        requestUuid={requestUuid}
        openDate={openDate}
        months={months}
        loanValue={loanValue}
      />
      <RequestForecastItemAdd
        projectUuid={project.uuid}
        requestUuid={requestUuid}
        minDate={openDate}
        forecast={currentForecast}
        loanValue={loanValue}
      />
      <Box>
        <SummaryRow
          isDrawdowns
          count={drawdowns.length}
          total={drawdownsTotalAmount}
          loanValue={loanValue}
        />
        {Boolean(repayments.length) && (
          <SummaryRow
            count={repayments.length}
            total={totalNumberFromArray(repayments, 'amount')}
          />
        )}
      </Box>
    </>
  )
}

const SummaryRow = ({
  isDrawdowns,
  count,
  total,
  loanValue,
}: SummaryRowProps) => {
  const overlimitError =
    isDrawdowns && total !== loanValue ? (
      total > loanValue ? (
        <>
          Estimation is higher than
          <br />
          the loan amount for {formatMoney(total - loanValue, {cents: true})}
        </>
      ) : (
        <>
          Estimation is less than
          <br />
          the loan amount for {formatMoney(loanValue - total, {cents: true})}
        </>
      )
    ) : undefined

  return (
    <div className={styles.summary}>
      <div />
      <Space>
        {isDrawdowns ? <RotateArrowThirdIcon /> : <RotateArrowFourthIcon />}
        <Text weight="demiBold">
          {maybePluralize(count, isDrawdowns ? 'drawdown' : 'repayment')}
        </Text>
      </Space>
      <Box align="right">
        <CentsMoney
          value={total}
          size="3xl"
          weight="medium"
          color={overlimitError ? 'redError' : undefined}
        />
        {overlimitError && (
          <Text size="xs" color="redError" weight="medium" mt={6}>
            {overlimitError}
          </Text>
        )}
      </Box>
    </div>
  )
}
